import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import type { Product } from '../../types/product-variant';
import { useMedia } from '../../context/MediaContext';
import { pickProductMediaUrl, pickVariantImageUrl } from '../../utils/media';
import { cloudinaryUrl } from '../../utils/cloudinary';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../context/LocaleContext';

export const SIMILARITY_SCORES = {
    SAME_MATERIAL: 3,
    SAME_CATEGORY: 2,
    SAME_COLLECTION: 2,
};

export const getRelatedProducts = (
    currentProduct: Product,
    allProducts: Product[],
    cartItems: { id: string }[],
    maxProducts: number = 12
): Product[] => {
    const cartItemIds = cartItems.map(item => item.id);
    const availableProducts = allProducts.filter(p => 
        p.id !== currentProduct.id && !cartItemIds.includes(p.id)
    );

    const productsWithScores = availableProducts.map(product => {
        let score = 0;
        
        if (product.material === currentProduct.material) {
            score += SIMILARITY_SCORES.SAME_MATERIAL;
        }
        
        if (product.category === currentProduct.category) {
            score += SIMILARITY_SCORES.SAME_CATEGORY;
        }
        
        if (product.collection === currentProduct.collection) {
            score += SIMILARITY_SCORES.SAME_COLLECTION;
        }

        return { product, score };
    });

    return productsWithScores
        .sort((a, b) => b.score - a.score)
        .slice(0, maxProducts)
        .map(item => item.product);
};

interface RelatedProductsProps {
    products: Product[];
}

const RelatedProducts = ({ products }: RelatedProductsProps) => {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isReady, setIsReady] = useState(false);
    const { getMediaByProduct, loading: mediaLoading } = useMedia();
    const itemWidth = isMobile ? Math.round(window.innerWidth * 0.66) : 280;
    const itemMargin = isMobile ? 1 : 12;
    const itemsToShow = isMobile ? 2 : 4;
    const [scrollPosition, setScrollPosition] = useState(0);
    const { t } = useTranslation();
    const { localePath } = useLocale();

    const totalWidth = (itemWidth + itemMargin) * products.length;

    // Helper to get product image from Media table with fallbacks
    const getProductMainImage = (product: Product): string => {
        // Don't query media until it's loaded
        if (!mediaLoading) {
            // Try to get from Media table
            let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
            if (media.length === 0) {
              media = getMediaByProduct(product.id);
            }
            if (media.length === 0) {
              media = getMediaByProduct(product.slug);
            }

            // Try to find product-main usage
            if (media.length > 0) {
                const mainImage = pickProductMediaUrl(media, ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal'], 'product-main');
                if (mainImage) return mainImage;
            }
        }

        // Fallback to variant image
        const variantMain = pickVariantImageUrl(product.variants?.[0]?.images, 0);
        if (variantMain) return variantMain;

        return '';
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Initial setup effect
    useEffect(() => {
        if (carouselRef.current) {
            carouselRef.current.scrollLeft = 0;
            setScrollPosition(0);
            
            // Add a small delay to ensure DOM is fully rendered
            setTimeout(() => {
                setIsReady(true);
            }, 100);
        }
    }, [products]);

    const scrollCarousel = (direction: 'left' | 'right', itemsToScroll: number = 2) => {
        if (carouselRef.current) {
            const scrollAmount = direction === 'left' ? -itemWidth * itemsToScroll : itemWidth * itemsToScroll;
            carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            setScrollPosition(prev => prev + scrollAmount);
        }
    };

    // Calculate arrow visibility
    const showLeftArrow = scrollPosition > 0;
    const showRightArrow = carouselRef.current && isReady ? 
        totalWidth - carouselRef.current.clientWidth > scrollPosition : 
        false;

    return (
        <div className="mt-8 md:mt-20 mb-4 md:mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-black dark:text-white mb-4 md:mb-6 px-4 md:px-0">
                {t('product.youMightAlsoLike')}
            </h2>
            
            <div className="relative w-screen left-1/2 right-1/2 -mx-[50vw] ml-[calc(-50vw+0px)] mr-[calc(-50vw+0px)]">
                <div className="relative w-full mx-auto px-0 md:px-4">
                    {isReady && (
                        <>
                            {showLeftArrow && (
                                <button
                                    onClick={() => scrollCarousel('left', itemsToShow)}
                                    className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/80 dark:bg-brand-dark/80 backdrop-blur-sm text-brand-dark dark:text-white p-2 rounded-full shadow-lg hover:bg-white dark:hover:bg-brand-dark transition-colors"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
                                </button>
                            )}
                            {showRightArrow && (
                                <button
                                    onClick={() => scrollCarousel('right', itemsToShow)}
                                    className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/80 dark:bg-brand-dark/80 backdrop-blur-sm text-brand-dark dark:text-white p-2 rounded-full shadow-lg hover:bg-white dark:hover:bg-brand-dark transition-colors"
                                >
                                    <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                                </button>
                            )}
                        </>
                    )}

                    <div className="relative overflow-hidden">
                        {/* Feather Overlays — desktop only */}
                        {showLeftArrow && (
                            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-brand-sand dark:from-brand-dark via-brand-sand/80 dark:via-brand-dark/80 to-transparent z-10 pointer-events-none" />
                        )}
                        {showRightArrow && (
                            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-brand-sand dark:from-brand-dark via-brand-sand/80 dark:via-brand-dark/80 to-transparent z-10 pointer-events-none" />
                        )}

                        <div 
                            ref={carouselRef}
                            className="overflow-x-scroll overflow-y-hidden scrollbar-hide relative"
                        >
                            <div className="flex gap-px md:gap-3 px-0 md:px-8">
                                {products.map((product) => (
                                    <Link
                                        key={product.id}
                                        to={localePath('/product/:slug', { slug: product.slug })}
                                        className="flex-none"
                                        style={{ 
                                            width: `${itemWidth}px`,
                                        }}
                                        onClick={() => window.scrollTo(0, 0)}
                                    >
                                        <motion.div 
                                            whileHover={{ scale: 1.05 }}
                                            className="bg-white dark:bg-brand-dark rounded-none md:rounded-xl shadow-none md:shadow-md overflow-hidden relative"
                                        >
                                            <img
                                                src={cloudinaryUrl(getProductMainImage(product), 400)}
                                                alt={product.name}
                                                className={`w-full object-cover ${
                                                    isMobile ? 'aspect-square' : 'h-64'
                                                }`}
                                                loading="lazy"
                                                width={isMobile ? 300 : 600}
                                                height={isMobile ? itemWidth : 256}
                                            />
                                            {/* Mobile overlay text */}
                                            {isMobile && (
                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2">
                                                    <h3 className="text-sm font-medium text-white truncate">{product.name}</h3>
                                                    <p className="text-xs text-white/90">
                                                        {product.variants && product.variants.length > 0
                                                            ? `${t('product.from')} €${Math.min(...product.variants.map(v => v.price)).toFixed(2)}`
                                                            : `€${(product.price ?? 0).toFixed(2)}`
                                                        }
                                                    </p>
                                                </div>
                                            )}
                                            {/* Desktop text area */}
                                            {!isMobile && (
                                                <div className="p-4">
                                                    <h3 className="text-lg font-semibold text-black dark:text-white mb-2 truncate">
                                                        {product.name}
                                                    </h3>
                                                    <p className="text-base text-black dark:text-white font-medium">
                                                        {product.variants && product.variants.length > 0
                                                            ? `${t('product.from')} €${Math.min(...product.variants.map(v => v.price)).toFixed(2)}`
                                                            : `€${(product.price ?? 0).toFixed(2)}`
                                                        }
                                                    </p>
                                                </div>
                                            )}
                                            {product.oneOfKind && (
                                                <div className="absolute top-2 right-2 bg-brand-blue text-white text-xs px-2 py-1 rounded z-10">
                                                    {t('product.oneOfKindBadge')}
                                                </div>
                                            )}
                                            {product.variants && product.variants.length > 0 && (
                                                <div className="absolute top-2 left-2 bg-brand-blue text-white text-xs px-2 py-1 rounded z-10">
                                                    {t('product.multipleOptions')}
                                                </div>
                                            )}
                                        </motion.div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RelatedProducts; 